import React, { useEffect, useState } from "react";
import { UpsertPartnerQuoteInfoRequest } from "../api/types";
import QuoteOutcomeDisclosures from "./QuoteOutcomeDisclosures";
import { getDisclosures, getTelematicsDisclosures } from "../config/quote-outcome/Disclosures";
import AddressSearch from "./address-search";
import DatePicker from "./date-picker";
import { defaultQuoteOutcomeHighlightMap } from "../api/types/quote-outcome";
import { EndorsementInfo } from "./endorsement-info";

type TelematicsInputsProps = {
  idPrefix: string;
  hasElectronicLoggingDevice?: string;
  telematicsProgramType?: string;
  telematicsSelection?: string;
  handleInput: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => void;
};

const TelematicsInputs: React.FC<TelematicsInputsProps> = ({
  idPrefix,
  hasElectronicLoggingDevice,
  telematicsProgramType,
  telematicsSelection,
  handleInput,
}) => (
  <form>
    <fieldset className="form-field radio-button-group">
      <legend>Do you have an ELD?</legend>
      <div className="endorsement-radio-container">
        <input
          id={`eld-yes-${idPrefix}`}
          type="radio"
          name="hasElectronicLoggingDevice"
          value="yes"
          checked={hasElectronicLoggingDevice === "yes"}
          onChange={handleInput}
        />
        <label className="radio endorsement-radio-label" htmlFor={`eld-yes-${idPrefix}`}>
          <span>Yes</span>
        </label>
        <input
          id={`eld-no-${idPrefix}`}
          type="radio"
          name="hasElectronicLoggingDevice"
          value="no"
          checked={hasElectronicLoggingDevice === "no"}
          onChange={handleInput}
        />
        <label className="radio endorsement-radio-label" htmlFor={`eld-no-${idPrefix}`}>
          <span>No</span>
        </label>
      </div>
    </fieldset>
    <div style={{ marginBottom: "4px" }}>
      <label className="text" htmlFor={`telematicsProgramType-${idPrefix}`}>
        Telematics Program Type
      </label>
      <div className="select-box">
        <select
          id={`telematicsProgramType-${idPrefix}`}
          name="telematicsProgramType"
          value={telematicsProgramType ?? ""}
          onChange={handleInput}
        >
          <option value="">Select</option>
          <option value="DriveEasyProELD">DriveEasy Pro ELD</option>
          <option value="DriveEasyProOBD2">DriveEasy Pro OBD2</option>
          <option value="DriveEasyProDashcam">DriveEasy Pro Dashcam</option>
          <option value="N/A">N/A</option>
        </select>
      </div>
    </div>
    <fieldset className="form-field radio-button-group" style={{ marginBottom: "4px" }}>
      <legend>Telematics Selection</legend>
      <div className="endorsement-radio-container">
        <input
          id={`telematics-selection-yes-${idPrefix}`}
          type="radio"
          name="telematicsSelection"
          value="yes"
          checked={telematicsSelection === "yes"}
          onChange={handleInput}
        />
        <label className="radio endorsement-radio-label" htmlFor={`telematics-selection-yes-${idPrefix}`}>
          <span>Yes</span>
        </label>
        <input
          id={`telematics-selection-no-${idPrefix}`}
          type="radio"
          name="telematicsSelection"
          value="no"
          checked={telematicsSelection === "no"}
          onChange={handleInput}
        />
        <label className="radio endorsement-radio-label" htmlFor={`telematics-selection-no-${idPrefix}`}>
          <span>No</span>
        </label>
      </div>
    </fieldset>
  </form>
);

interface EndorsementRow {
  id: string;
  lob: string;
  policyNumber: string;
  status: string;
}

export const DEFAULT_REQUIRED_FIELDS = [
  "quoteOutcome", "zipCode", "stateCode", "emailId", "phoneNumber", "lastName",
  "dateOfBirth", "referenceNumber", "source", "lineOfBusiness", "leadSource",
  "language", "businessName", "businessAddress", "businessClassDescription",
];

export const QUOTE_OUTCOME_DEPENDENT_FIELDS = [
  "policyNumber", "referenceNumber", "effectiveDate", "expirationDate", "premium",
  "soldPremium", "paymentPlan", "paymentConfirmationNumber", "typeOfQuote", "subType",
  "primaryOutcomeType", "secondaryOutcomeType",
];

export const NOT_QUOTED_REQUIRED_FIELDS: Record<string, string[]> = {
  Misdirect: ["primaryOutcomeType", "secondaryOutcomeType"],
  Incomplete: ["primaryOutcomeType"],
  Decline: ["primaryOutcomeType", "firstName", "lastName", "phoneNumber", "referenceNumber", "agentNotes"],
};

export const NOT_QUOTED_RESET_FIELDS: Record<string, string[]> = {
  Misdirect: ["zipCode", "stateCode"],
  Incomplete: ["zipCode", "stateCode"],
};

export const OUTCOME_REQUIRED_FIELDS: Record<string, string[]> = {
  Quote: [...DEFAULT_REQUIRED_FIELDS, "primaryOutcomeType", "premium"],
  Sale: [...DEFAULT_REQUIRED_FIELDS, "effectiveDate", "premium"],
  "Follow-Up": [...DEFAULT_REQUIRED_FIELDS],
};

export function applyNotQuotedEffect(
  primaryOutcomeType: string,
  setHighlightedFields: React.Dispatch<React.SetStateAction<{ [key: string]: boolean }>>,
  resetQuoteOutcomeFields: (fields: string[]) => void,
  setRequiredFields: React.Dispatch<React.SetStateAction<string[]>>
) {
  setHighlightedFields(defaultQuoteOutcomeHighlightMap);
  const resetFields = NOT_QUOTED_RESET_FIELDS[primaryOutcomeType];
  if (resetFields) resetQuoteOutcomeFields(resetFields);
  setRequiredFields(NOT_QUOTED_REQUIRED_FIELDS[primaryOutcomeType] ?? ["primaryOutcomeType"]);
}

export function applySourceEffect(
  source: string,
  resetQuoteOutcomeFields: (fields: string[]) => void,
  setRequiredFields: React.Dispatch<React.SetStateAction<string[]>>
) {
  if (source === "GEICO_SSP") {
    setRequiredFields(prev => [...prev, "paymentPlan", "expirationDate"]);
  } else {
    resetQuoteOutcomeFields(["paymentPlan", "expirationDate"]);
    setRequiredFields(prev => prev.filter(f => f !== "paymentPlan" && f !== "expirationDate"));
  }
  if (source === "CNA_SURETY") {
    setRequiredFields(prev => [...prev, "paymentConfirmationNumber"]);
  } else {
    resetQuoteOutcomeFields(["paymentConfirmationNumber"]);
    setRequiredFields(prev => prev.filter(f => f !== "paymentConfirmationNumber"));
  }
}

export function highlightBorder(highlighted: boolean | undefined): React.CSSProperties | undefined {
  return highlighted ? { borderColor: 'red' } : undefined;
}

type Props = React.InputHTMLAttributes<HTMLInputElement> & {
  selectedOutcome: string;
  upsertQuoteInputs: UpsertPartnerQuoteInfoRequest;
  validationErrors: UpsertPartnerQuoteInfoRequest;
  handleInput: (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => void;
  lineOfBusinessOptions: string[];
  leadSourceOptions: string[];
  languageOptions: string[];
  setRequiredFields: React.Dispatch<React.SetStateAction<string[]>>;
  resetQuoteOutcomeFields: (fields: string[]) => void;
  isFixedSource?: boolean;
  redirectAnswer?: boolean;
  carrierCodes: { value: string; label: string }[];
  horizontalLayout?: boolean;
  isRPMAuthorization?: boolean;
  highlightedFields: Record<string, boolean>;
  setHighlightedFields: React.Dispatch<React.SetStateAction<{ [key: string]: boolean }>>;
  onEndorsementDataChange?: (hasEndorsements: boolean, endorsements: EndorsementRow[]) => void;
  referenceNumber?: string;
  endorsementLobs?: string[];
  isPrefillMode?: boolean;
  endorsementLobNames?: string[];
};

export const QuoteOutcomeFields: React.FC<Props> = ({
  selectedOutcome,
  upsertQuoteInputs,
  validationErrors,
  handleInput,
  lineOfBusinessOptions,
  leadSourceOptions,
  languageOptions,
  setRequiredFields,
  resetQuoteOutcomeFields,
  isFixedSource,
  redirectAnswer,
  carrierCodes,
  horizontalLayout,
  isRPMAuthorization,
  highlightedFields = {},
  setHighlightedFields,
  onEndorsementDataChange,
  referenceNumber,
  endorsementLobs,
  isPrefillMode,
  endorsementLobNames
}) => {
  const ls = horizontalLayout ? 'main-page' : 'sidebar';
  const effectiveDateId = `effectiveDate-${ls}`;
  const expirationDateId = `expirationDate-${ls}`;
  const salesDateId = `salesDate-${ls}`;
  const [dateFieldResetKey, setDateFieldResetKey] = useState(0);
  const [endorsementLobsState, setEndorsementLobsState] = useState<string[]>([]);
  const [endorsementOutcomeTypesState, setEndorsementOutcomeTypesState] = useState<string[]>([]);

  const isGEICOSource = upsertQuoteInputs.source?.includes("GEICO") ?? false;
  const telematicsDisclosures = getTelematicsDisclosures(
    upsertQuoteInputs.hasElectronicLoggingDevice,
    upsertQuoteInputs.telematicsProgramType,
    upsertQuoteInputs.telematicsSelection,
    upsertQuoteInputs.source
  );
  const telematicsDisclosureKey = `${upsertQuoteInputs.hasElectronicLoggingDevice}-${upsertQuoteInputs.telematicsProgramType}-${upsertQuoteInputs.telematicsSelection}`;
  const layoutId = ls;

  const today = new Date();
  const minDateObj = new Date(today);
  minDateObj.setFullYear(today.getFullYear() - 2);
  const maxDateObj = new Date(today);
  maxDateObj.setFullYear(today.getFullYear() + 2);
  const format = (d: Date) =>
    `${String(d.getMonth() + 1).padStart(2, "0")}/${String(d.getDate()).padStart(2, "0")}/${d.getFullYear()}`;
  const minDate = format(minDateObj);
  const maxDate = format(maxDateObj);

  const isSale = selectedOutcome === "Sale";
  const isSaleGeicoSsp = isSale && upsertQuoteInputs.source === "GEICO_SSP";
  const isSaleCnaSurety = isSale && upsertQuoteInputs.source === "CNA_SURETY";
  const layoutFieldClass = horizontalLayout ? "partner-quote-page-field" : "";

  const selectedCarrierValue = carrierCodes.find(carrier =>
    carrier.value === upsertQuoteInputs.source ||
    carrier.value.split(/[/_]/).includes(upsertQuoteInputs.source)
  )?.value ?? "";

  const lobList = [
    ...(upsertQuoteInputs.lineOfBusiness ? [upsertQuoteInputs.lineOfBusiness] : []),
    ...(endorsementLobs ?? endorsementLobsState),
  ].filter(l => l.trim() !== "");
  const combinedLobs = lobList.length > 0 ? lobList : undefined;

  const outcomeList = [
    ...(upsertQuoteInputs.primaryOutcomeType ? [upsertQuoteInputs.primaryOutcomeType] : []),
    ...endorsementOutcomeTypesState,
  ].filter(t => t.trim() !== "");
  const combinedOutcomeTypes = outcomeList.length > 0 ? outcomeList : undefined;

  const normalDisclosures = getDisclosures(
    upsertQuoteInputs.quoteOutcome,
    combinedLobs,
    upsertQuoteInputs.stateCode,
    upsertQuoteInputs.source,
    combinedOutcomeTypes,
    isRPMAuthorization
  );
  const currentDisclosureNamesKey = [
    ...normalDisclosures.map(d => d.DisclosureName),
    ...telematicsDisclosures.map(d => d.DisclosureName),
  ].join("|");

  const handleEndorsementDataChange = (hasEndorsements: boolean, endorsements: EndorsementRow[]) => {
    setEndorsementLobsState(endorsements.filter(e => e.lob.trim() !== "").map(e => e.lob));
    setEndorsementOutcomeTypesState(endorsements.filter(e => e.status.trim() !== "").map(e => e.status));
    onEndorsementDataChange?.(hasEndorsements, endorsements);
  };

  useEffect(() => {
    const preservedFields: Record<string, string | undefined> = {
      effectiveDate: upsertQuoteInputs.effectiveDate,
      expirationDate: upsertQuoteInputs.expirationDate,
      salesDate: upsertQuoteInputs.salesDate,
      referenceNumber: upsertQuoteInputs.referenceNumber,
    };
    const fieldsToReset = QUOTE_OUTCOME_DEPENDENT_FIELDS.filter(f => !preservedFields[f]);
    resetQuoteOutcomeFields(fieldsToReset);
  }, [selectedOutcome]);

  useEffect(() => {
    if (selectedOutcome === "Not Quoted") {
      applyNotQuotedEffect(upsertQuoteInputs.primaryOutcomeType, setHighlightedFields, resetQuoteOutcomeFields, setRequiredFields);
      return;
    }
    const fields = OUTCOME_REQUIRED_FIELDS[selectedOutcome];
    if (fields) setRequiredFields(fields);
    if (!upsertQuoteInputs.effectiveDate && !upsertQuoteInputs.expirationDate) {
      setDateFieldResetKey((prev) => prev + 1);
    }
  }, [selectedOutcome, upsertQuoteInputs.primaryOutcomeType]);

  useEffect(() => {
    if (selectedOutcome !== "Sale") return;
    applySourceEffect(upsertQuoteInputs.source, resetQuoteOutcomeFields, setRequiredFields);
  }, [selectedOutcome, upsertQuoteInputs.source]);

  useEffect(() => {
    const currentSet = new Set(currentDisclosureNamesKey ? currentDisclosureNamesKey.split("|") : []);

    const fields: Array<[
      "disclosuresRead" | "disclosuresSelectedAsYes" | "disclosuresSelectedAsNo" | "disclosuresSelectedAsNA",
      string[]
    ]> = [
      ["disclosuresRead", upsertQuoteInputs.disclosuresRead],
      ["disclosuresSelectedAsYes", upsertQuoteInputs.disclosuresSelectedAsYes],
      ["disclosuresSelectedAsNo", upsertQuoteInputs.disclosuresSelectedAsNo],
      ["disclosuresSelectedAsNA", upsertQuoteInputs.disclosuresSelectedAsNA],
    ];

    fields.forEach(([fieldName, current]) => {
      const filtered = (current ?? []).filter((name) => currentSet.has(name));
      if (filtered.length !== (current ?? []).length) {
        handleInput({
          target: { name: fieldName, value: filtered },
        } as unknown as React.ChangeEvent<HTMLInputElement>);
      }
    });
  }, [
    currentDisclosureNamesKey,
    upsertQuoteInputs.disclosuresRead,
    upsertQuoteInputs.disclosuresSelectedAsYes,
    upsertQuoteInputs.disclosuresSelectedAsNo,
    upsertQuoteInputs.disclosuresSelectedAsNA,
    handleInput,
  ]);

  if (!selectedOutcome) {
    return null;
  }

  return (
      <div>
        <div className={horizontalLayout ? "partner-quote-page-quote-outcome-container" : ""}>
          <div className={layoutFieldClass}>
            <label className="text" htmlFor={`source-${ls}`}>
              Source
            </label>
            <div>
              <div className="select-box">
                <select
                  id={`source-${ls}`}
                  name="source"
                  value={selectedCarrierValue}
                  onChange={handleInput}
                  disabled={isFixedSource && redirectAnswer}
                  style={highlightBorder(highlightedFields.source)}
                >
                  <option value="">Select</option>
                  {carrierCodes.map((carrier) => (
                    <option key={carrier.value} value={carrier.value}>
                      {carrier.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className={`${layoutFieldClass} partner-quote-field-container`}>
            <label className="text" htmlFor={`referenceNumber-${ls}`}>
              {isSale ? 'Commercial Policy #' : 'Reference#'}
            </label>
            <div>
              <input
                id={`referenceNumber-${ls}`}
                type="text"
                name="referenceNumber"
                value={upsertQuoteInputs.referenceNumber}
                onChange={handleInput}
                style={highlightBorder(highlightedFields.referenceNumber)}
              />
            </div>
            {validationErrors.referenceNumber && (
              <div className="error-message">{validationErrors.referenceNumber}</div>
            )}
          </div>

          <div className={layoutFieldClass}>
            <label className="text" htmlFor={`lineOfBusiness-${ls}`}>
              Line of Business
            </label>
            <div>
              <div className="select-box">
                <select
                  id={`lineOfBusiness-${ls}`}
                  value={upsertQuoteInputs.lineOfBusiness}
                  name="lineOfBusiness"
                  onChange={handleInput}
                  disabled={redirectAnswer === true}
                  style={highlightBorder(highlightedFields.lineOfBusiness)}
                >
                  <option value="">Select</option>
                  {lineOfBusinessOptions.map((lobName) => (
                    <option key={lobName} value={lobName}>
                      {lobName}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {isSale && (
            <div className={`${layoutFieldClass} partner-quote-field-container`}>
              <div>
                <div className="datepicker-container" style={highlightBorder(highlightedFields.effectiveDate)}>
                  <DatePicker
                    key={`effective-date-${dateFieldResetKey}`}
                    value={upsertQuoteInputs.effectiveDate || ""}
                    minDate={minDate}
                    maxDate={maxDate}
                    onChange={handleInput}
                    inputId={effectiveDateId}
                    name="effectiveDate"
                    labelText="Effective Date"
                    highlight={highlightedFields.effectiveDate}
                  />
                </div>
                {validationErrors.effectiveDate && (
                  <div className="error-message">{validationErrors.effectiveDate}</div>
                )}
              </div>
            </div>
          )}

          {isSaleGeicoSsp && (
            <div className={`${layoutFieldClass} partner-quote-field-container`}>
              <div>
                <div className="datepicker-container">
                  <DatePicker
                    key={`expiration-date-${dateFieldResetKey}`}
                    value={upsertQuoteInputs.expirationDate || ""}
                    minDate={minDate}
                    maxDate={maxDate}
                    onChange={handleInput}
                    inputId={expirationDateId}
                    name="expirationDate"
                    labelText="Expiration Date"
                    highlight={highlightedFields.expirationDate}
                  />
                </div>
                {validationErrors.expirationDate && (
                  <div className="error-message">{validationErrors.expirationDate}</div>
                )}
              </div>
            </div>
          )}

          {isSale && (
            <div className={`${layoutFieldClass} partner-quote-field-container`}>
              <div>
                <div className="datepicker-container" style={highlightBorder(highlightedFields.salesDate)}>
                  <DatePicker
                    key={`sales-date-${dateFieldResetKey}`}
                    value={upsertQuoteInputs.salesDate || ""}
                    minDate={minDate}
                    maxDate={maxDate}
                    onChange={handleInput}
                    inputId={salesDateId}
                    name="salesDate"
                    labelText="Sales Date"
                    highlight={highlightedFields.salesDate}
                  />
                </div>
                {validationErrors.salesDate && (
                  <div className="error-message">{validationErrors.salesDate}</div>
                )}
              </div>
            </div>
          )}

          {selectedOutcome !== "Not Quoted" && selectedOutcome !== "Follow-Up" && (
            <div className={`${layoutFieldClass} partner-quote-field-container`}>
              <label className="text" htmlFor={`premium-${ls}`}>
                Premium
              </label>
              <div>
                <input
                  id={`premium-${ls}`}
                  type="text"
                  name="premium"
                  value={upsertQuoteInputs.premium}
                  onChange={handleInput}
                  style={highlightBorder(highlightedFields.premium)}
                />
              </div>
              {validationErrors.premium && (
                <div className="error-message">{validationErrors.premium}</div>
              )}
            </div>
          )}

          {isSaleGeicoSsp && (
            <div className={layoutFieldClass}>
              <label className="text" htmlFor={`paymentPlan-${ls}`}>
                Payment Plan
              </label>
              <div>
                <input
                  id={`paymentPlan-${ls}`}
                  type="text"
                  name="paymentPlan"
                  value={upsertQuoteInputs.paymentPlan}
                  onChange={handleInput}
                  style={highlightBorder(highlightedFields.paymentPlan)}
                />
                {validationErrors.paymentPlan && (
                  <div className="error-message">{validationErrors.paymentPlan}</div>
                )}
              </div>
            </div>
          )}

          {isSaleCnaSurety && (
            <div className={layoutFieldClass}>
              <label className="text" htmlFor={`paymentConfirmationNumber-${ls}`}>
                <strong>Payment Confirmation Number</strong>
              </label>
              <div>
                <input
                  id={`paymentConfirmationNumber-${ls}`}
                  type="text"
                  name="paymentConfirmationNumber"
                  value={upsertQuoteInputs.paymentConfirmationNumber}
                  onChange={handleInput}
                  style={highlightBorder(highlightedFields.paymentConfirmationNumber)}
                />
                {validationErrors.paymentConfirmationNumber && (
                  <div className="error-message">{validationErrors.paymentConfirmationNumber}</div>
                )}
              </div>
            </div>
          )}

          <div className={layoutFieldClass}>
            <label className="text" htmlFor={`leadSource-${ls}`}>
              Lead Source
            </label>
            <div>
              <div className="select-box">
                <select
                  id={`leadSource-${ls}`}
                  name="leadSource"
                  value={upsertQuoteInputs.leadSource || "N/A"}
                  onChange={handleInput}
                  style={highlightBorder(highlightedFields.leadSource)}
                >
                  <option value="">Select</option>
                  {leadSourceOptions.map((leadSource) => (
                    <option key={leadSource} value={leadSource}>
                      {leadSource}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            {validationErrors.leadSource && (
              <div className="error-message">{validationErrors.leadSource}</div>
            )}
          </div>

          <div className={layoutFieldClass}>
            <label className="text" htmlFor={`language-${ls}`}>
              Language
            </label>
            <div>
              <div className="select-box">
                <select
                  id={`language-${ls}`}
                  name="language"
                  value={upsertQuoteInputs.language}
                  onChange={handleInput}
                  style={highlightBorder(highlightedFields.language)}
                >
                  <option value="">Select</option>
                  {languageOptions.map((language) => (
                    <option key={language} value={language}>
                      {language}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className={layoutFieldClass}>
            <label className="text" htmlFor={`businessName-${ls}`}>
              Business Name
            </label>
            <div>
              <input
                id={`businessName-${ls}`}
                type="text"
                name="businessName"
                value={upsertQuoteInputs.businessName}
                onChange={handleInput}
                style={highlightBorder(highlightedFields.businessName)}
              />
            </div>
          </div>

          <div className={layoutFieldClass}>
            <label className="text" htmlFor="businessAddress">
              Business Address
            </label>
            <div>
              <AddressSearch
                id="businessAddress"
                value={upsertQuoteInputs.businessAddress}
                setFieldValue={(field, value) =>
                  handleInput({ target: { name: field, value } } as React.ChangeEvent<HTMLInputElement>)
                }
                enteredZip={upsertQuoteInputs.zipCode}
                highlight={highlightedFields.businessAddress}
                horizontalLayout={horizontalLayout ?? false}
              />
            </div>
          </div>

          <div className={layoutFieldClass}>
            <label className="text" htmlFor={`businessClassDescription-${ls}`}>
              Business Class Description
            </label>
            <div>
              <input
                id={`businessClassDescription-${ls}`}
                type="text"
                name="businessClassDescription"
                value={upsertQuoteInputs.businessClassDescription}
                onChange={handleInput}
                style={highlightBorder(highlightedFields.businessClassDescription)}
              />
            </div>
            {!isPrefillMode && !horizontalLayout && (
              <div key="endorsement-sidebar">
                <EndorsementInfo
                  lineOfBusinessOptions={endorsementLobNames ?? lineOfBusinessOptions}
                  referenceNumber={referenceNumber}
                  horizontalLayout={false}
                  onDataChange={handleEndorsementDataChange}
                />
              </div>
            )}
            {!horizontalLayout && (
              <div>
                <h4>Read Disclosures</h4>
                <QuoteOutcomeDisclosures
                  disclosures={normalDisclosures}
                  handleInput={handleInput}
                  upsertQuoteInputs={upsertQuoteInputs}
                  horizontalLayout={false}
                />
              </div>
            )}
            {!horizontalLayout && isGEICOSource && (
              <div>
                <h4>Telematics</h4>
                <TelematicsInputs
                  idPrefix={layoutId}
                  hasElectronicLoggingDevice={upsertQuoteInputs.hasElectronicLoggingDevice}
                  telematicsProgramType={upsertQuoteInputs.telematicsProgramType}
                  telematicsSelection={upsertQuoteInputs.telematicsSelection}
                  handleInput={handleInput}
                />
              </div>
            )}
            {!horizontalLayout && telematicsDisclosures.length > 0 && (
              <div>
                <h4>ELD/Telematics Disclosures</h4>
                <QuoteOutcomeDisclosures
                  key={telematicsDisclosureKey}
                  disclosures={telematicsDisclosures}
                  handleInput={handleInput}
                  upsertQuoteInputs={upsertQuoteInputs}
                  horizontalLayout={false}
                />
              </div>
            )}
          </div>
        </div>
        {horizontalLayout && !isPrefillMode && (
          <div key="endorsement-main-page" style={{ maxWidth: "500px", paddingLeft: "3rem", paddingRight: "1.5rem", marginLeft: "-20px" }}>
            <EndorsementInfo
              lineOfBusinessOptions={endorsementLobNames ?? lineOfBusinessOptions}
              referenceNumber={referenceNumber}
              horizontalLayout={true}
              onDataChange={handleEndorsementDataChange}
            />
          </div>
        )}
        {horizontalLayout && (
          <div>
            <h4>Read Disclosures</h4>
            <QuoteOutcomeDisclosures
              disclosures={normalDisclosures}
              handleInput={handleInput}
              upsertQuoteInputs={upsertQuoteInputs}
              horizontalLayout={true}
            />
          </div>
        )}
        {horizontalLayout && isGEICOSource && (
          <div style={{ maxWidth: "500px", paddingLeft: "3rem", paddingRight: "1.5rem", marginLeft: "-20px" }}>
            <h4>Telematics</h4>
            <TelematicsInputs
              idPrefix={layoutId}
              hasElectronicLoggingDevice={upsertQuoteInputs.hasElectronicLoggingDevice}
              telematicsProgramType={upsertQuoteInputs.telematicsProgramType}
              telematicsSelection={upsertQuoteInputs.telematicsSelection}
              handleInput={handleInput}
            />
          </div>
        )}
        {horizontalLayout && telematicsDisclosures.length > 0 && (
          <div>
            <h4>ELD/Telematics Disclosures</h4>
            <QuoteOutcomeDisclosures
              key={telematicsDisclosureKey}
              disclosures={telematicsDisclosures}
              handleInput={handleInput}
              upsertQuoteInputs={upsertQuoteInputs}
              horizontalLayout={true}
            />
          </div>
        )}
      </div>
  );
};

export default QuoteOutcomeFields;

